import * as React from 'react';
import { Field } from 'redux-form';

import AccountPreview from 'af-root/scenes/Company/Settings/Members/Shared/AccountsField/AccountPreview';

import type { W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM, W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User } from 'ab-api/web/account/findAllSuperintendentsAndProjectManagersInCompanyWithEmail';

import type { DivisionViewModel, GroupedDivisions } from 'ab-viewModels/division.viewModel';
import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';
import type JobStatusVM from 'ab-viewModels/jobStatus/option.viewModel';
import type LocationVM from 'ab-viewModels/location/option.viewModel';

import * as UserUtils from '@acceligentllc/shared/utils/user';

import type { PriorityItem } from '@acceligentllc/shared/enums/priority';
import { PriorityList } from '@acceligentllc/shared/enums/priority';

import * as ColorUtils from 'af-utils/color.util';

import { SegmentLabel } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import DropdownNoRedux from 'af-components/Controls/Dropdown';
import PriorityLabel from 'af-components/PriorityLabel';

import JobUpsertFM from '../../formModel';
import type { InvoiceReminderRecipientFM } from '../../formModel';
import { formSectionClass, formSectionClassRow, formSectionClassColumn2, formSectionClassColumn4, formSectionDropdownItem, formSectionDropdownItemMain, formSectionDropdownItemSecondary, formSectionClassColumn6 } from '../../helpers';
import styles from '../../styles.module.scss';

type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	change: (field: string, value: any) => void;
	findJobStatuses: () => Promise<JobStatusVM[]>;
	findAllProjectManagers: () => Promise<EmployeeOptionVM[]>;
	findAllSisAndPMs: () => Promise<W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM>;
	findAllLocations: () => Promise<LocationVM[]>;
	findAllDivisions: () => Promise<GroupedDivisions>;
	isDisabled: boolean;
	invoiceReminderRecipients: InvoiceReminderRecipientFM[];
	workRequestId: number;
};

const filterOption = (option: EmployeeOptionVM, rawInput: string) => {
	const { firstName, lastName, uniqueId } = option;
	return `${firstName} ${lastName} ${uniqueId}`.toLowerCase().includes(rawInput.toLowerCase());
};

const filterInvoiceRecipientsOption = (option: W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User, rawInput: string) => {
	const { fullName, uniqueId } = option;
	return `${fullName} ${uniqueId}`.toLowerCase().includes(rawInput.toLowerCase());
};

const renderJobStatus = (option: JobStatusVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={[formSectionDropdownItemMain, ColorUtils.getColorTextClass(option.color)].join(' ')}>
			{option.name}
		</div>
		<div className={formSectionDropdownItemSecondary}>
			{option.description}
		</div>
	</div>;

const renderSelectedJobStatus = (option: JobStatusVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={[formSectionDropdownItemMain, ColorUtils.getColorTextClass(option.color)].join(' ')}>
			{option.name}
		</div>
	</div>;

const renderEmployeeOption = (option: EmployeeOptionVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={formSectionDropdownItemMain}>
			{`${option.firstName} ${option.lastName} (${option.uniqueId})`}
		</div>
	</div>;

const renderInvoiceRecipientOption = (option: W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User) =>
	<div className={formSectionDropdownItem} key={option.accountId}>
		<div className={formSectionDropdownItemMain}>
			{`${option.fullName} (${option.uniqueId})`}
		</div>
	</div>;

const renderSelectedProjectManager = (option: EmployeeOptionVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={formSectionDropdownItemMain}>
			{`${option.firstName} ${option.lastName} (${option.uniqueId})`}
		</div>
	</div>;

const renderOffice = (option: LocationVM) => <div key={option.id}>{option.nickname}</div>;

const renderDivision = (option: DivisionViewModel) => <div key={option.id}>{option.name}</div>;

const JobSummarySection: React.FC<Props> = ({
	change,
	findJobStatuses,
	findAllProjectManagers,
	findAllSisAndPMs,
	findAllLocations,
	findAllDivisions,
	isDisabled,
	invoiceReminderRecipients,
}) => {
	const renderPriorityItem = React.useCallback((option: PriorityItem) => <PriorityLabel priority={option.id} />, []);
	const [jobStatuses, setJobStatuses] = React.useState<JobStatusVM[]>([]);
	const [projectManagers, setProjectManagers] = React.useState<EmployeeOptionVM[]>([]);
	const [allSIsAndPMs, setAllSIsAndPMs] = React.useState<W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User[]>([]);
	const [availableSIsAndPMs, setAvailableSIsAndPMs] = React.useState<W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User[]>([]);
	const [offices, setOffices] = React.useState<LocationVM[]>([]);
	const [divisions, setDivisions] = React.useState<DivisionViewModel[]>([]);
	const [invoiceRecipientsUpdated, setInvoiceRecipientsUpdated] = React.useState
		<W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User[]>([]);

	const fetchInvoiceReminderRecipientsOptions = React.useCallback(async () => {
		const fetchedrecipients = await findAllSisAndPMs();
		setAllSIsAndPMs(fetchedrecipients.users ?? []);
	}, [findAllSisAndPMs]);

	React.useEffect(() => {
		fetchInvoiceReminderRecipientsOptions();
	}, [fetchInvoiceReminderRecipientsOptions]);

	React.useEffect(() => {
		const currentInvoiceReminderRecipients: W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User[] = invoiceReminderRecipients?.map(
			(_recipient) => {
				return {
					accountId: _recipient.accountId,
					uniqueId: _recipient.uniqueId,
					firstName: _recipient.firstName,
					lastName: _recipient.lastName,
					fullName: UserUtils.getPlainFullName(_recipient.firstName, _recipient.lastName),
					phoneNumber: null,
					email: _recipient.email,
					accountDeletedAtFlag: _recipient.accountDeletedAtFlag,
				};
			});
		setInvoiceRecipientsUpdated(currentInvoiceReminderRecipients);
	}, [invoiceReminderRecipients]);

	React.useEffect(() => {
		const invoiceReminderRecipientsAccountIds = invoiceReminderRecipients?.map((_recipient) => _recipient.accountId);
		const SIsAndPMs = allSIsAndPMs.filter((_acc) => !invoiceReminderRecipientsAccountIds.includes(_acc.accountId));

		if (SIsAndPMs.length - availableSIsAndPMs.length !== 0) {
			setAvailableSIsAndPMs(SIsAndPMs);
		}

	}, [allSIsAndPMs, availableSIsAndPMs, invoiceReminderRecipients]);

	const lazyLoadJobStatuses = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedJobStatuses = await findJobStatuses();
			setJobStatuses(fetchedJobStatuses ?? []);
		}
	}, [findJobStatuses]);

	const lazyLoadOffices = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedOffices = await findAllLocations();
			setOffices(fetchedOffices ?? []);
		}
	}, [findAllLocations]);

	const lazyLoadProjectManagers = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedProjectManagers = await findAllProjectManagers();
			setProjectManagers(fetchedProjectManagers ?? []);
		}
	}, [findAllProjectManagers]);

	const lazyLoadDivisions = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedDivisions = await findAllDivisions();
			setDivisions(Object.values<DivisionViewModel[]>(fetchedDivisions).flat());
		}
	}, [findAllDivisions]);

	const removeAccount = React.useCallback((index: number) => {
		const accountToUnselect = invoiceRecipientsUpdated[index];
		const updatedListOfSelectedRecipients = invoiceRecipientsUpdated.filter((_recipient) => _recipient.accountId !== accountToUnselect.accountId);
		change(JobUpsertFM.getAttributeName('invoiceReminderRecipients'), updatedListOfSelectedRecipients);

	}, [change, invoiceRecipientsUpdated]);

	const selectInvoiceRecipient = React.useCallback((option: W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User) => {
		change(JobUpsertFM.getAttributeName('invoiceReminderRecipients'), [
			...invoiceReminderRecipients ?? [],
			{ ...option, phoneNumber: null },
		]);

	}, [change, invoiceReminderRecipients]);

	const checkboxStyle = React.useMemo(() => [formSectionClassColumn2, styles['job-form__tab-content__form-section__row__centered-column']].join(' '), []);
	const invoiceRecipientPillParent = React.useMemo(() => [formSectionClassColumn6, styles['job-form__tab-content__form-section__row__pill-parent']].join(' '), []);
	const invoiceRecipientPill = React.useMemo(() => styles['job-form__tab-content__form-section__row__recipient-pill'], []);

	return (
		<div className={formSectionClass}>
			<SegmentLabel
				label="Job Summary"
				tooltip="A summary of the key details associated with this job."
			/>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="jobCode"
						isRequired={true}
						label="Job ID"
						name={JobUpsertFM.getAttributeName('jobCode')}
						placeholder="Enter Job ID"
						type="text"
					/>
				</div>
				<div className={formSectionClassColumn4}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="title"
						isRequired={true}
						label="Job Title"
						name={JobUpsertFM.getAttributeName('title')}
						placeholder="Enter Job Title"
						type="text"
					/>
				</div>
				<div className={checkboxStyle}>
					<Field
						component={Checkbox}
						id="isInternal"
						isDisabled={isDisabled}
						label="Internal Job"
						name={JobUpsertFM.getAttributeName('isInternal')}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="customerCompany"
						isRequired={true}
						label="Customer Company"
						name={JobUpsertFM.getAttributeName('customerCompanyName')}
						placeholder="Enter Customer Company"
						type="text"
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="customerReferencePO"
						label="Customer Reference/PO"
						name={JobUpsertFM.getAttributeName('customerReferencePO')}
						placeholder="Enter Customer Reference/PO"
						type="text"
					/>
				</div>
				<div className={checkboxStyle}>
					<Field
						component={Checkbox}
						id="isEmergencyJob"
						isDisabled={isDisabled}
						label="Emergency Job"
						name={JobUpsertFM.getAttributeName('isEmergencyJob')}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						disabled={isDisabled}
						id="priority"
						label="Priority *"
						name="priority"
						options={PriorityList}
						renderMenuItem={renderPriorityItem}
						valueKey="id"
						withCaret={true}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						disabled={isDisabled}
						hasBlankOption={true}
						id="jobStatusId"
						label="Job Status"
						name={JobUpsertFM.getAttributeName('jobStatusId')}
						onLazyLoad={lazyLoadJobStatuses}
						options={jobStatuses}
						placeholder="Select Status"
						propName="jobStatus"
						renderMenuItem={renderJobStatus}
						renderSelected={renderSelectedJobStatus}
						valueKey="id"
						withCaret={true}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						disabled={isDisabled}
						filterable={true}
						filterBy={filterOption}
						id="projectManagerId"
						label="Project Manager"
						name="projectManagerId"
						onLazyLoad={lazyLoadProjectManagers}
						options={projectManagers}
						placeholder="Select Project Manager"
						propName="projectManager"
						renderMenuItem={renderEmployeeOption}
						renderSelected={renderSelectedProjectManager}
						valueKey="id"
						withCaret={true}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						disabled={isDisabled}
						id="officeId"
						label="Office *"
						name="officeId"
						onLazyLoad={lazyLoadOffices}
						options={offices}
						placeholder="Select Office"
						propName="office"
						renderMenuItem={renderOffice}
						valueKey="id"
						withCaret={true}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						disabled={isDisabled}
						hasBlankOption={true}
						id="divisionId"
						label="Division"
						name="divisionId"
						onLazyLoad={lazyLoadDivisions}
						options={divisions}
						placeholder="Select Division"
						propName="division"
						renderMenuItem={renderDivision}
						valueKey="id"
						withCaret={true}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<DropdownNoRedux<W_Account_FindSuperintendentsAndProjectManagersWithEmail_VM_User>
						disabled={isDisabled}
						filterable={true}
						filterBy={filterInvoiceRecipientsOption}
						fixed={true}
						forcePlaceholder={true}
						isWhite={true}
						label="Invoice Reminder Recipient"
						onValueChange={selectInvoiceRecipient}
						options={availableSIsAndPMs}
						placeholder="Select Recipient"
						renderMenuItem={renderInvoiceRecipientOption}
						tooltipMessage="Only superintendents and project managers with an email address can be selected."
						valueKey="accountId"
						withBorder={true}
						withCaret={true}
					/>
				</div>
				<div className={invoiceRecipientPillParent}>
					{!!invoiceRecipientsUpdated?.length && invoiceRecipientsUpdated.map((_recipientData, _index) => {
						const recipientDataModified = {
							id: _recipientData.accountId,
							..._recipientData,
						};
						const hasEmail = !!_recipientData.email;
						const isDeactivated = !!_recipientData.accountDeletedAtFlag;
						return (
							<div className={invoiceRecipientPill} key={_recipientData.accountId}>
								<AccountPreview
									account={recipientDataModified}
									hasEmail={hasEmail}
									index={_index}
									isDeactivated={isDeactivated}
									isDisabled={isDisabled}
									key={`account#${_recipientData}`}
									removeAccount={removeAccount}
									willReceiveNotification={hasEmail && !isDeactivated}
								/>
							</div>
						);
					})}
				</div>
			</div>
		</div >
	);
};

export default React.memo(JobSummarySection);
